import React, { useEffect, useMemo, useRef, useState } from "react";
import Avatar from "./Avatar";
import { msPhotoUrl } from "../../utils/avatars";

// Combobox de pessoas (diretório de colaboradores): digita o nome, escolhe na
// lista e preenche nome + e-mail. Continua aceitando texto livre (solicitante
// externo que não está no diretório). Mobile-friendly: dropdown w-full + scroll.
const norm = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // remove acentos
    .toLowerCase();

export default function PersonAutocomplete({
  people = [],
  name = "",
  onChange, // ({ name, email }) => void  — email só vem quando escolhe da lista
  placeholder = "Nome do solicitante",
  inputClassName = "",
  "data-testid": testId,
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);

  const fullName = (p) => `${p.first_name || ""} ${p.last_name || ""}`.trim();

  const matches = useMemo(() => {
    const q = norm(name);
    if (!q) return [];
    const scored = people
      .map((p) => ({ p, hay: norm(`${fullName(p)} ${p.email || ""} ${p.job_title || ""}`) }))
      .filter(({ hay }) => hay.includes(q))
      // nomes que COMEÇAM com o termo primeiro
      .sort((a, b) => {
        const an = norm(fullName(a.p)).startsWith(q) ? 0 : 1;
        const bn = norm(fullName(b.p)).startsWith(q) ? 0 : 1;
        return an - bn;
      })
      .slice(0, 8)
      .map(({ p }) => p);
    return scored;
  }, [people, name]);

  // Fecha ao clicar fora.
  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (p) => {
    onChange?.({ name: fullName(p), email: p.email || "" });
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e) => {
    if (!open || matches.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      pick(matches[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <input
        value={name}
        onChange={(e) => {
          onChange?.({ name: e.target.value }); // texto livre; não mexe no e-mail
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open && matches.length > 0}
        aria-autocomplete="list"
        className={
          inputClassName ||
          "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        }
        data-testid={testId}
      />
      {open && matches.length > 0 && (
        <ul className="absolute z-30 mt-1 w-full max-h-72 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {matches.map((p, i) => {
            const full = fullName(p);
            return (
              <li key={p.id}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(p)}
                  className={`flex w-full items-center gap-2.5 px-3 py-2 text-left ${
                    i === active ? "bg-blue-50" : "hover:bg-gray-50"
                  }`}
                >
                  <Avatar
                    src={msPhotoUrl(p.email, full)}
                    name={full}
                    className="h-7 w-7 shrink-0"
                    textClassName="text-[11px]"
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-gray-800">{full}</span>
                    <span className="block truncate text-xs text-gray-500">
                      {[p.job_title, p.email].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
